import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, ArrowRight, Check, Copy, RotateCcw, Calculator } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import ToolCallout from '../../components/ToolCallout';
import { trackMoveScoreCompleted, trackScoreEmailCaptured } from '../../lib/analytics';
import { attachUtmToUser } from '../../lib/utm';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const SITE_URL = 'https://mundalo.com';

// ============== QUIZ DATA ==============
const QUESTIONS = [
  {
    id: 'q1',
    eyebrow: 'TELL US ABOUT YOUR FINANCES',
    prompt: 'What is your monthly income or budget for living in Mexico?',
    options: [
      { value: 'A', label: 'Under $1,500 per month' },
      { value: 'B', label: '$1,500 to $2,500 per month' },
      { value: 'C', label: '$2,500 to $4,400 per month' },
      { value: 'D', label: '$4,400 to $7,400 per month' },
      { value: 'E', label: 'Over $7,400 per month' },
    ],
  },
  {
    id: 'q2',
    eyebrow: 'TELL US ABOUT YOUR DESTINATION',
    prompt: 'Which city or region are you most drawn to?',
    options: [
      { value: 'A', label: 'Mexico City / CDMX', city: 'Mexico City' },
      { value: 'B', label: 'Merida / Yucatan', city: 'Merida' },
      { value: 'C', label: 'Puerto Vallarta / Jalisco', city: 'Puerto Vallarta' },
      { value: 'D', label: 'San Miguel de Allende', city: 'San Miguel de Allende' },
      { value: 'E', label: 'Oaxaca / southern Mexico', city: 'Oaxaca' },
      { value: 'F', label: 'Beach destinations - Tulum, Playa, Cancun', city: 'your beach destination' },
      { value: 'G', label: 'Not sure yet', city: null },
    ],
  },
  {
    id: 'q3',
    eyebrow: 'TELL US ABOUT YOUR MOVE',
    prompt: 'Who is making this move with you?',
    options: [
      { value: 'A', label: 'Just me' },
      { value: 'B', label: 'Me and my partner' },
      { value: 'C', label: 'Me, my partner, and our kids' },
      { value: 'D', label: 'Me and a parent or family member' },
      { value: 'E', label: 'I have not decided yet' },
    ],
  },
  {
    id: 'q4',
    eyebrow: 'TELL US ABOUT YOUR TIMELINE',
    prompt: 'When are you planning to make the move?',
    options: [
      { value: 'A', label: 'Within the next 3 months' },
      { value: 'B', label: '3 to 6 months from now' },
      { value: 'C', label: '6 to 12 months from now' },
      { value: 'D', label: 'More than a year away' },
      { value: 'E', label: 'Just exploring for now' },
    ],
  },
  {
    id: 'q5',
    eyebrow: 'TELL US ABOUT YOUR INCOME',
    prompt: 'How is your income structured?',
    options: [
      { value: 'A', label: 'Remote job or employer payroll' },
      { value: 'B', label: 'Freelance or self-employed' },
      { value: 'C', label: 'Pension or Social Security' },
      { value: 'D', label: 'Investments or passive income' },
      { value: 'E', label: 'I am not sure if it qualifies' },
    ],
  },
  {
    id: 'q6',
    eyebrow: 'TELL US ABOUT YOUR CONCERNS',
    prompt: 'What is your biggest concern about making this move?',
    options: [
      { value: 'A', label: 'Getting the right visa' },
      { value: 'B', label: 'Understanding the true cost' },
      { value: 'C', label: 'Healthcare and insurance' },
      { value: 'D', label: 'Finding housing in Mexico' },
      { value: 'E', label: 'Building community and connection' },
      { value: 'F', label: 'I am pretty confident - I just need the plan' },
    ],
  },
];

// ============== SCORING ==============
const Q1_POINTS = { A: 10, B: 18, C: 26, D: 33, E: 35 };
const Q2_POINTS = (v) => (v === 'G' ? 8 : 20);
const Q4_POINTS = { A: 18, B: 20, C: 15, D: 10, E: 5 };
const Q5_POINTS = { A: 15, B: 10, C: 15, D: 12, E: 5 };
const Q3_MOD = { A: 0, B: 2, C: 0, D: 2, E: 0 };

const computeScore = (a) => {
  const fin = Q1_POINTS[a.q1] ?? 0;
  const loc = Q2_POINTS(a.q2);
  const tl = Q4_POINTS[a.q4] ?? 0;
  const inc = Q5_POINTS[a.q5] ?? 0;
  const mod = Q3_MOD[a.q3] ?? 0;
  const base = fin + loc + tl + inc + mod;
  const scaled = Math.min(99, Math.round((base * 100) / 92));
  return {
    score: scaled,
    financial: fin,
    location: loc,
    timeline: tl,
    income: inc,
    family: mod,
  };
};

const labelForScore = (s) => {
  if (s >= 90) return 'READY TO MOVE';
  if (s >= 75) return 'NEARLY THERE';
  if (s >= 50) return 'GETTING READY';
  return 'EARLY EXPLORER';
};

const colorForScore = (s) => {
  if (s >= 75) return '#2D6A4F';
  if (s >= 50) return '#2CA8A0';
  return '#E8A830';
};

const summaryFor = (a) => {
  const city = QUESTIONS[1].options.find((o) => o.value === a.q2)?.city || 'your destination';
  const q1HighIncome = a.q1 === 'D' || a.q1 === 'E';
  const q1LowIncome = a.q1 === 'A' || a.q1 === 'B';
  const q1Mid = a.q1 === 'C';
  const q2Specific = a.q2 !== 'G';
  const q2Unsure = a.q2 === 'G';
  const q4Fast = a.q4 === 'A' || a.q4 === 'B';
  const q4Mid = a.q4 === 'C';
  const q4Slow = a.q4 === 'D' || a.q4 === 'E';

  let base = '';
  if (q1HighIncome && q2Specific && q4Fast) {
    base = `Your income qualifies you for Temporary Residency and your timeline is aggressive in the best way. ${city} is a strong match for what you are describing. Your Blueprint will take this from a strong foundation to a complete action plan.`;
  } else if (q1LowIncome && q2Unsure && q4Slow) {
    base = `You are earlier in the journey than some but further along than you think. Your income will work in Oaxaca, Queretaro, or Merida where comfortable living starts around $1,000 per month. Your Blueprint identifies exactly what needs to happen before you move and in what order.`;
  } else if (q1Mid && q2Specific && q4Mid) {
    base = `Your income puts you near the Temporary Residency threshold and your timeline gives you enough runway to prepare your documentation properly. ${city} fits your budget well. Your Blueprint will map the visa process and the financial documentation you need to qualify.`;
  } else if (q1LowIncome && q2Specific && q4Fast) {
    base = `Your budget is on the tighter side but ${city} has options that work. Your timeline is ambitious which means starting your Blueprint now is the right move. Your Blueprint identifies the cities and neighborhoods that fit your specific budget.`;
  } else if (q1HighIncome && q2Unsure && (a.q4 === 'C' || a.q4 === 'D')) {
    base = `Your financial position is strong - you qualify for Temporary Residency and can live very comfortably in any Mexican city. Choosing your destination is the next decision to make. Your Blueprint compares your top city options side by side.`;
  } else if (q1Mid && q4Slow) {
    base = `You are in the research phase with solid financial potential. Your income is near the Temporary Residency threshold and you have time to close any gaps before you apply. Your Blueprint maps the exact path from where you are now to landing in Mexico.`;
  } else {
    base = `Based on your answers you are building toward a strong position to make this move. Your Blueprint will give you the personalized clarity you need - visa pathway, city selection, real budget, and your 30-day action plan.`;
  }

  if (a.q3 === 'B') base += ' Moving with your partner adds important considerations around dependent visas and household costs that your Blueprint covers in detail.';
  if (a.q3 === 'C') base += ' Moving with your family adds important considerations around dependent visas and household costs that your Blueprint covers in detail.';
  if (a.q3 === 'D') base += ' Moving with a family member adds important considerations around dependent visas and household costs that your Blueprint covers in detail.';
  return base;
};

const concernText = (q6) => ({
  A: 'The visa process is where most people stall. Your Blueprint includes a step-by-step visa section built for your specific income situation and chosen consulate.',
  B: 'Cost is the most common unknown. Your Blueprint includes a full budget table built for your specific city and lifestyle - not a generic average.',
  C: 'Healthcare in Mexico is significantly better than most Americans expect and significantly cheaper. Your Blueprint covers your exact options based on your age and destination.',
  D: 'Finding housing remotely is one of the hardest parts. Your Blueprint includes vetted housing contacts already in your destination city.',
  E: 'Community is what keeps people after the move. Your Blueprint includes access to the Mundalo community - Americans already living in your destination city.',
  F: 'You have the right mindset. Now you need the plan. Your Blueprint takes your readiness score and turns it into a 30-day action checklist.',
}[q6]);

// ============== CONVERSION COPY ==============
const conversionFor = (score) => {
  if (score >= 75) {
    return {
      eyebrow: 'YOU ARE READY',
      headline: 'You just need the plan.',
      body: `Your score of ${score} tells us you have the fundamentals. Your Blueprint turns that into a 19-section personalized relocation plan - your visa pathway, your neighborhood deep dive, your actual budget, your healthcare setup, and your 30-day action checklist. Built for your situation specifically.`,
      primary: { label: 'Get Your Blueprint - From $199', to: '/relocation/blueprint' },
      secondary: { label: 'Or start with 15 days free', to: '/start' },
    };
  }
  if (score >= 50) {
    return {
      eyebrow: 'YOU ARE CLOSER THAN YOU THINK',
      headline: 'A plan closes the gap.',
      body: `Your score of ${score} means you have the right instincts but gaps in the specifics. Your Blueprint identifies exactly what needs to happen before you move and builds you a step-by-step action plan to close those gaps.`,
      primary: { label: 'Get Your Blueprint - From $199', to: '/relocation/blueprint' },
      secondary: { label: 'Or start with 15 days free', to: '/start' },
    };
  }
  return {
    eyebrow: 'YOU ARE IN THE RIGHT PLACE',
    headline: 'Everyone starts somewhere.',
    body: `Your score of ${score} tells us you are still in the research phase - and that is completely fine. Start with the free community to connect with people who were exactly where you are right now. When you are ready your Blueprint will map the full path.`,
    primary: { label: 'Join Free for 15 Days', to: '/start' },
    secondary: { label: 'Or get your Blueprint now', to: '/relocation/blueprint' },
  };
};

// ============== UI ==============
const ProgressBar = ({ current, total }) => (
  <div className="mb-8" data-testid="quiz-progress">
    <p className="font-mono uppercase text-[10px] tracking-[0.18em] text-[#0A1F2E]/55 mb-2">
      Question {current} of {total}
    </p>
    <div className="flex gap-1.5">
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          className={`h-1 flex-1 rounded-full ${i < current ? 'bg-[#E8A830]' : 'bg-[#0A1F2E]/10'}`}
        />
      ))}
    </div>
  </div>
);

const OptionCard = ({ value, label, selected, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`w-full text-left px-5 py-4 rounded-xl border-2 transition group ${
      selected
        ? 'bg-[#E8A830] border-[#E8A830] text-[#0A1F2E]'
        : 'bg-white border-[#E0D6B8] text-[#0A1F2E] hover:border-[#E8A830] hover:bg-[#E8A830]/5'
    }`}
  >
    <span className="flex items-center gap-3">
      <span
        className={`shrink-0 w-7 h-7 rounded-full border-2 flex items-center justify-center font-mono text-xs font-semibold ${
          selected ? 'bg-[#0A1F2E] text-[#F9F6EF] border-[#0A1F2E]' : 'border-[#0A1F2E]/30 text-[#0A1F2E]/60 group-hover:border-[#E8A830]'
        }`}
      >
        {value}
      </span>
      <span className="text-base">{label}</span>
    </span>
  </button>
);

const ScoreLargeNumber = ({ score, color }) => (
  <div className="text-center" data-testid="score-display">
    <p
      className="font-display italic text-[#0A1F2E] leading-none"
      style={{ fontSize: 'clamp(96px, 14vw, 180px)' }}
      data-testid="score-number"
    >
      {score}
    </p>
    <p className="text-[#0A1F2E]/55 text-sm mt-2">out of 100</p>
    <p
      className="font-mono uppercase text-xs tracking-[0.22em] mt-3"
      style={{ color }}
      data-testid="score-label"
    >
      {labelForScore(score)}
    </p>
    <div className="mt-5 max-w-md mx-auto h-2.5 bg-[#0A1F2E]/8 rounded-full overflow-hidden">
      <div
        className="h-full rounded-full transition-all duration-700"
        style={{ width: `${score}%`, backgroundColor: color }}
      />
    </div>
  </div>
);

const BreakdownTile = ({ label, score, total, testId }) => {
  const pct = (score / total) * 100;
  return (
    <div className="bg-white border border-[#E0D6B8] rounded-xl p-5" data-testid={testId}>
      <p className="font-mono uppercase text-[10px] tracking-[0.15em] text-[#0A1F2E]/55 mb-2">{label}</p>
      <p className="font-display italic text-3xl text-[#0A1F2E] leading-none">
        {score}
        <span className="text-base text-[#0A1F2E]/45 font-body not-italic ml-1">/ {total}</span>
      </p>
      <div className="mt-3 h-1.5 bg-[#0A1F2E]/8 rounded-full overflow-hidden">
        <div className="h-full bg-[#2CA8A0]" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

// ============== MAIN ==============
const MoveScore = () => {
  const [searchParams] = useSearchParams();

  // Lazy initial state: read URL prefill once at mount. Spec says
  // `?start=qN&qN=value` pre-answers that question; quiz still starts
  // at q1 and shows all questions in sequence per spec.
  const [answers, setAnswers] = useState(() => {
    const init = {};
    for (const q of QUESTIONS) {
      const v = searchParams.get(q.id);
      if (v && q.options.some((o) => o.value === v)) init[q.id] = v;
    }
    return init;
  });
  const [step, setStep] = useState(0); // always start at q1 per spec
  const [history, setHistory] = useState([]); // for back navigation

  const onSelect = (qid, val) => {
    setAnswers((prev) => ({ ...prev, [qid]: val }));
    setHistory((h) => [...h, step]);
    setTimeout(() => setStep((s) => s + 1), 300);
  };

  const goBack = () => {
    setHistory((h) => {
      const next = [...h];
      const last = next.pop();
      if (typeof last === 'number') setStep(last);
      return next;
    });
  };

  const reset = () => {
    setAnswers({});
    setStep(0);
    setHistory([]);
    window.scrollTo(0, 0);
  };

  const showingResults = step >= QUESTIONS.length;
  const current = QUESTIONS[step];
  const scoreResult = useMemo(() => (showingResults ? computeScore(answers) : null), [showingResults, answers]);

  return (
    <div className="min-h-screen bg-[#F9F6EF] flex flex-col">
      <Helmet>
        <title>Are You Ready to Move to Mexico? Free Readiness Score | Mundalo</title>
        <meta name="description" content="Take the free Mexico Readiness Assessment. 6 questions, instant personalized score, and your next steps. Updated for 2026 by Mundalo." />
        <link rel="canonical" href={`${SITE_URL}/mexico-readiness-score`} />
        <meta property="og:title" content="Find Your Mexico Readiness Score - Free Assessment by Mundalo" />
        <meta property="og:description" content="6 questions. Your personal score out of 100. Find out exactly where you stand and what you need to move to Mexico." />
        <meta property="og:url" content={`${SITE_URL}/mexico-readiness-score`} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Find Your Mexico Readiness Score - Free Assessment by Mundalo" />
        <meta name="twitter:description" content="6 questions. Your personal score out of 100." />
      </Helmet>

      <Header />

      <main className="flex-1">
        <section className="pt-16 sm:pt-24 pb-12 px-6 md:px-10">
          <div className="max-w-3xl mx-auto text-center">
            <p className="font-mono uppercase text-[11px] tracking-[0.2em] text-[#2CA8A0] mb-4">
              Free Mundalo tool · 2026
            </p>
            <h1 className="font-display italic text-5xl sm:text-6xl md:text-7xl text-[#0A1F2E] leading-[1.04] mb-6">
              Are You Ready to Move to Mexico?
            </h1>
            <p className="text-lg sm:text-xl text-[#0A1F2E]/75 leading-relaxed">
              6 questions. Instant results. Your personal readiness score out of 100.
            </p>
          </div>
        </section>

        <section className="px-6 md:px-10 pb-20">
          <div className="max-w-2xl mx-auto">
            {!showingResults && current && (
              <div className="bg-white border border-[#E0D6B8] rounded-2xl p-6 sm:p-8 shadow-sm" data-testid="quiz-card">
                <ProgressBar current={step + 1} total={QUESTIONS.length} />
                <p className="font-mono uppercase text-[11px] tracking-[0.18em] text-[#2CA8A0] mb-2" data-testid="question-eyebrow">
                  {current.eyebrow}
                </p>
                <h2 className="font-display italic text-3xl sm:text-4xl text-[#0A1F2E] leading-tight mb-6" data-testid="question-prompt">
                  {current.prompt}
                </h2>
                <div className="space-y-3">
                  {current.options.map((opt) => (
                    <OptionCard
                      key={opt.value}
                      value={opt.value}
                      label={opt.label}
                      selected={answers[current.id] === opt.value}
                      onClick={() => onSelect(current.id, opt.value)}
                      testId={`option-${current.id}-${opt.value}`}
                    />
                  ))}
                </div>
                {step > 0 && (
                  <button
                    type="button"
                    onClick={goBack}
                    data-testid="back-button"
                    className="mt-6 inline-flex items-center gap-2 text-sm text-[#0A1F2E]/65 hover:text-[#0A1F2E] transition"
                  >
                    <ArrowLeft className="w-4 h-4" /> Back
                  </button>
                )}
              </div>
            )}

            {showingResults && scoreResult && (
              <Results
                answers={answers}
                result={scoreResult}
                onReset={reset}
              />
            )}
          </div>
        </section>

        {!showingResults && <SEOContent />}
      </main>

      <Footer />
    </div>
  );
};

// ============== RESULTS ==============
// iter 139 - Per-tool sessionStorage key prefix. We use a key that includes
// the score so users re-running the quiz with different answers still get a
// fresh card. Picked the prefix so it does not collide with the GA4 dedup
// keys (which start with `ga4_`).
const SCORE_EMAIL_SUBMITTED_KEY = (score) => `tool_score_submitted:${score}`;

const Results = ({ answers, result, onReset }) => {
  const [email, setEmail] = useState('');
  const submittedAlready = (() => {
    try {
      return typeof window !== 'undefined'
        && window.sessionStorage.getItem(SCORE_EMAIL_SUBMITTED_KEY(result.score)) === '1';
    } catch { return false; }
  })();
  const [emailSent, setEmailSent] = useState(submittedAlready);
  const [emailing, setEmailing] = useState(false);
  const [copied, setCopied] = useState(false);

  const summary = summaryFor(answers);
  const concern = concernText(answers.q6);
  const conversion = conversionFor(result.score);
  const color = colorForScore(result.score);
  const city = QUESTIONS[1].options.find((o) => o.value === answers.q2)?.city || '';

  // iter 136 - fire GA4 `move_score_completed` the moment results render.
  // sessionStorage-deduped inside the helper, so a refresh does not double-fire.
  useEffect(() => {
    trackMoveScoreCompleted({
      score: result.score,
      score_label: labelForScore(result.score),
      q2_city: answers.q2,
      q6_concern: answers.q6,
    });
  }, [result.score, answers.q2, answers.q6]);

  const copyShareLink = async () => {
    const text = `I just got a ${result.score}/100 on the Mundalo Mexico Readiness Score. Find out yours at mundalo.com/mexico-readiness-score`;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (e) {
      // noop - clipboard not available
    }
  };

  const saveScore = async (e) => {
    e.preventDefault();
    if (!email.trim() || !email.includes('@')) return;
    setEmailing(true);
    try {
      const r = await fetch(`${API_URL}/api/tools/save-score`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          score: result.score,
          q6: answers.q6,
          q2: answers.q2,
          city,
          breakdown: {
            financial: result.financial,
            location: result.location,
            timeline: result.timeline,
            income: result.income,
            family: result.family,
          },
          summary,
          concern,
        }),
      });
      if (r.ok) {
        setEmailSent(true);
        try { window.sessionStorage.setItem(SCORE_EMAIL_SUBMITTED_KEY(result.score), '1'); } catch { /* sessionStorage blocked */ }
        // iter 136 - track email capture conversion event (deduped per score)
        trackScoreEmailCaptured({ score: result.score });
        // iter 144h - attach first-touch UTM to the just-created user row.
        attachUtmToUser({ email: email.trim().toLowerCase(), useAuth: false });
      }
    } catch (err) {
      // silent - user can retry
    } finally {
      setEmailing(false);
    }
  };

  return (
    <div className="space-y-10" data-testid="results-section">
      <div className="bg-white border border-[#E0D6B8] rounded-2xl p-8 sm:p-12 shadow-sm">
        <ScoreLargeNumber score={result.score} color={color} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
        <BreakdownTile label="Financial Readiness" score={result.financial} total={35} testId="breakdown-financial" />
        <BreakdownTile label="Planning Clarity" score={result.location + result.timeline} total={40} testId="breakdown-planning" />
        <BreakdownTile label="Income & Visa" score={result.income} total={15} testId="breakdown-income" />
      </div>

      <div className="bg-white border border-[#E0D6B8] rounded-2xl p-6 sm:p-8" data-testid="results-summary">
        <p className="text-base sm:text-lg text-[#0A1F2E] leading-relaxed mb-4">{summary}</p>
        <p className="text-base text-[#0A1F2E]/75 leading-relaxed border-l-2 border-[#2CA8A0] pl-4">
          {concern}
        </p>
      </div>

      <div className="bg-[#0A1F2E] text-[#F9F6EF] rounded-2xl p-8 sm:p-10" data-testid="conversion-card">
        <p className="font-mono uppercase text-[11px] tracking-[0.2em] text-[#2CA8A0] mb-3">
          {conversion.eyebrow}
        </p>
        <h3 className="font-display italic text-4xl sm:text-5xl mb-5 leading-tight">
          {conversion.headline}
        </h3>
        <p className="text-base sm:text-lg leading-relaxed text-[#F9F6EF]/85 mb-7">
          {conversion.body}
        </p>
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
          <Link
            to={conversion.primary.to}
            data-testid="primary-cta"
            className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-[#E8A830] text-[#0A1F2E] font-mono uppercase text-xs tracking-[0.14em] rounded-full font-semibold hover:bg-[#f0b340] transition"
          >
            {conversion.primary.label}
            <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to={conversion.secondary.to}
            data-testid="secondary-cta"
            className="text-sm text-[#F9F6EF]/75 hover:text-[#F9F6EF] underline underline-offset-4"
          >
            {conversion.secondary.label}
          </Link>
        </div>
      </div>

      {/* Email capture - iter 139: updated copy. Card always shows on initial
          render; the success state persists for the session via sessionStorage. */}
      {!emailSent ? (
        <div className="bg-[#F9F6EF] border border-[#0A1F2E]/15 rounded-2xl p-6 sm:p-7" data-testid="email-capture">
          <p className="font-display italic text-2xl sm:text-3xl text-[#0A1F2E] mb-2 leading-tight">
            Your score tells a story. Where should we send the full version?
          </p>
          <p className="text-sm text-[#0A1F2E]/65 mb-4">
            Your complete results with what your score means for your visa, your budget, and your timeline.
          </p>
          <form onSubmit={saveScore} className="flex flex-col sm:flex-row gap-3">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              data-testid="email-input"
              className="flex-1 px-4 py-3 border border-[#E0D6B8] rounded-lg text-[#0A1F2E] bg-white focus:outline-none focus:border-[#2CA8A0]"
              required
            />
            <button
              type="submit"
              disabled={emailing}
              data-testid="save-score-submit"
              className="px-5 py-3 bg-[#E8A830] text-[#0A1F2E] font-mono uppercase text-xs tracking-[0.14em] rounded-full font-semibold hover:bg-[#f0b340] transition disabled:opacity-50"
            >
              {emailing ? 'Sending…' : 'Send My Results'}
            </button>
          </form>
          <p className="mt-3 text-xs text-[#0A1F2E]/55 italic">
            Planning this move with someone? Forward them your results.
          </p>
        </div>
      ) : (
        <div className="bg-[#2CA8A0]/10 border-2 border-[#2CA8A0] rounded-2xl p-6 text-center" data-testid="email-sent-confirm">
          <Check className="w-6 h-6 text-[#2CA8A0] mx-auto mb-2" />
          <p className="font-mono uppercase text-xs tracking-[0.18em] text-[#2CA8A0]">Sent. Check your inbox.</p>
        </div>
      )}

      {/* Share */}
      <div className="text-center" data-testid="share-section">
        <p className="text-sm text-[#0A1F2E]/65 mb-3">Share your score</p>
        <button
          type="button"
          onClick={copyShareLink}
          data-testid="copy-share-btn"
          className="inline-flex items-center gap-2 px-4 py-2 border border-[#0A1F2E]/20 rounded-full text-sm text-[#0A1F2E] hover:border-[#2CA8A0] hover:text-[#2CA8A0] transition"
        >
          {copied ? <Check className="w-4 h-4 text-[#2CA8A0]" /> : <Copy className="w-4 h-4" />}
          {copied ? 'Copied!' : 'Copy link'}
        </button>
      </div>

      {/* Action links */}
      <div className="flex flex-col sm:flex-row justify-center gap-4 text-sm text-[#0A1F2E]/65 pt-2">
        <button onClick={onReset} className="inline-flex items-center gap-1.5 hover:text-[#0A1F2E]" data-testid="retake-btn">
          <RotateCcw className="w-3.5 h-3.5" /> Retake the assessment
        </button>
        <span className="hidden sm:inline text-[#0A1F2E]/30">·</span>
        <Link to="/mexico-cost-of-living-calculator" className="inline-flex items-center gap-1.5 hover:text-[#0A1F2E]" data-testid="goto-calculator">
          <Calculator className="w-3.5 h-3.5" /> Calculate your cost of living
        </Link>
      </div>
    </div>
  );
};

// ============== SEO CONTENT ==============
const SEOContent = () => (
  <section className="px-6 md:px-10 pb-24" data-testid="seo-content">
    <div className="max-w-3xl mx-auto prose prose-lg">
      <h2 className="font-display italic text-3xl sm:text-4xl text-[#0A1F2E] mb-5">
        What Your Mexico Readiness Score Means
      </h2>
      <p className="text-[#0A1F2E]/80 leading-relaxed mb-4">
        The Mundalo Mexico Readiness Score measures four things: your financial position relative to Mexico&apos;s Temporary Residency thresholds, the clarity of your destination choice, the realism of your timeline, and the structure of your income. It does not measure how badly you want to go. It does not measure how lovely your Pinterest board is. It measures whether you could file a Temporary Residency application tomorrow and have it work.
      </p>
      <p className="text-[#0A1F2E]/80 leading-relaxed mb-4">
        Most people who take this assessment score somewhere between 55 and 80. That is the honest middle - they have an income that could qualify them, they have a city or two in mind, and they have a timeline that is real but not urgent. The Blueprint is built for exactly that range. It takes a &ldquo;getting close&rdquo; foundation and turns it into a sequenced action plan with visa documentation, neighborhood selection, and the financial paperwork the consulate actually asks for.
      </p>
      <p className="text-[#0A1F2E]/80 leading-relaxed mb-4">
        If you scored under 50, do not take it as a no. Take it as a &ldquo;not yet, and here is what to learn first.&rdquo; Most of what stops Americans from moving to Mexico is information, not money. Spend a month inside the Mundalo community before you spend a peso on relocation services. Watch how people who already moved talk about the decisions. Your score will move on its own.
      </p>
      <p className="text-[#0A1F2E]/80 leading-relaxed mb-4">
        If you scored over 75, you do not need more inspiration. You need execution. Your Blueprint takes the foundation you already have and turns it into a 30-day action checklist with the right consulate, the right documents, the right neighborhood, and the right healthcare setup for your situation. That is the whole game from here.
      </p>
      <p className="text-[#0A1F2E]/80 leading-relaxed">
        Need a next step? Read the{' '}
        <Link to="/relo-news/moving-to-mexico-checklist-2026" className="text-[#2CA8A0] underline underline-offset-2">Moving to Mexico Checklist 2026</Link>
        {' '}or run your numbers through the{' '}
        <Link to="/mexico-cost-of-living-calculator" className="text-[#2CA8A0] underline underline-offset-2">Mexico Cost of Living Calculator</Link>.
      </p>
      <div className="mt-10">
        <ToolCallout variant="cost" />
      </div>
    </div>
  </section>
);

export default MoveScore;
